import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@data-delivery/d3s';
import React from 'react';

export interface ConfirmDialogProps {
  trigger: React.ReactElement;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  confirmDisabled?: boolean;
  destructive?: boolean;
  className?: string;
  keepTriggerDropdownOpen?: boolean;
}

export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  confirmDisabled,
  destructive,
  className,
  keepTriggerDropdownOpen,
}: ConfirmDialogProps) {
  const enhancedTrigger = React.useMemo(() => {
    if (!keepTriggerDropdownOpen) {
      return trigger;
    }

    return React.cloneElement(trigger, {
      onSelect(e: unknown) {
        const evt = e as { preventDefault?: () => void };
        evt.preventDefault?.();
        if (typeof trigger.props.onSelect === 'function') {
          trigger.props.onSelect(e);
        }
      },
    });
  }, [keepTriggerDropdownOpen, trigger]);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{enhancedTrigger}</AlertDialogTrigger>
      <AlertDialogContent className={className}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              onCancel?.();
            }}
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            data-variant={destructive ? 'destructive' : undefined}
            disabled={confirmDisabled}
            onClick={() => {
              onConfirm?.();
            }}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
